/**
 * /claude/ — the copy, held in one file so it can be reviewed, sourced and
 * guarded in one place.
 *
 * ── TWO SWITCHES, BOTH OFF UNTIL THE OWNER SAYS ─────────────────────────────
 *
 * CLAUDE_PAGE_PUBLISHED: the page ships at its final URL, noindex, with no
 * sitemap entry and no inbound link, exactly as /sample-report/ did (see the
 * NOINDEX, UNLINKED note in SampleReportPage.jsx for why not a /preview/
 * route). Going live is this flag set true, which removes noindex and adds the
 * sitemap entry (scripts/lib/public-routes.mjs reads it), plus one nav or
 * footer link.
 *
 * CLAUDE_AI_SECTION_VISIBLE: the claude.ai set-up steps render only when this
 * is true. It was false until the owner confirmed the connector live.
 *
 * ── EVERY CLAIM IS SOURCED ──────────────────────────────────────────────────
 *
 * The line beside each claim names the code that makes it true.
 * backend/tests/test_claude_page_claims.py reads this file and fails if a
 * sourced value drifts from that code: the MCP URL, the scopes and their
 * defaults, the free allowance, the price, and the paid-audit gate.
 */

import { PLAN_PRICES } from '@/config/pricing';

export const CLAUDE_PAGE_PUBLISHED = true;
// true since 2026-10-05: the owner confirmed claude.ai live (list, get, the
// ownership refusal and the profile).
export const CLAUDE_AI_SECTION_VISIBLE = true;

// backend/oauth/config.py RESOURCE; the MCP endpoint is served at its root
// (backend/mcp_app/setup.py, streamable_http_path="/").
export const MCP_URL = 'https://mcp.uxauditpro.com';

export const CLAUDE_CODE_COMMAND = `claude mcp add --transport http uxauditpro ${MCP_URL}`;

export const SEO_COPY = {
  title: 'Use UXAuditPro inside Claude',
  description:
    'Connect UXAuditPro to Claude Code or claude.ai: list your audits, read findings, compare two audits and start your free monthly audit from Claude.',
};

export const HERO = {
  eyebrow: 'Claude connector',
  h1: 'Use UXAuditPro inside Claude',
  lead:
    'Connect your UXAuditPro account to Claude and work with your audits in the conversation: '
    + 'see what you have run, read the findings, and compare two runs of the same page.',
};

// One line per tool, in backend/mcp_app/tools.py.
export const CAPABILITIES = [
  { title: 'List your audits', body: 'Your recent audits, newest first, with their status and score.' }, // list_audits
  { title: 'Read an audit', body: "An audit's scores, findings and recommended fixes, the same ones your report shows you." }, // get_audit, trimmed as the web report is
  { title: 'Compare two audits', body: 'Which issues were resolved (not reported in the later run), which are new (not reported in the earlier run), and which are in both.' }, // compare_audits, EARLIER / LATER labels
  { title: 'Start a free audit', body: 'Start an audit of a live page using your free monthly audit.' }, // start_audit, audits:create
  { title: 'Paid audits, only with your approval', body: 'Claude can spend a paid credit only after you approve that one audit, every time.' }, // start_paid_audit: confirm_spend + destructiveHint
];

export const CLAUDE_CODE_STEPS = [
  { text: 'In your terminal, add the server:', code: CLAUDE_CODE_COMMAND },
  { text: 'In Claude Code, run', code: '/mcp' },
  { text: 'Pick uxauditpro, choose Authenticate, then sign in and approve in your browser.' },
];

export const CLAUDE_AI_STEPS = [
  { text: 'In claude.ai, open Settings, then Connectors.' },
  { text: 'Add a custom connector with this URL:', code: MCP_URL },
  { text: 'Keep the defaults, then choose Connect and approve in the window that opens.' },
];

// Each line: backend/oauth/config.py DEFAULT_GRANTED and SCOPE_DESCRIPTIONS;
// backend/mcp_app/tools.py start_paid_audit; frontend/src/components/ConnectedApps.js.
export const PERMISSIONS = [
  'By default Claude can see your audits and start your free monthly audit. Nothing else.',
  'Spending a paid audit credit is a separate permission, unticked when you connect, and Claude must ask you to approve each paid audit.',
  'Disconnect at any time from Connected apps on your Account page. Its access ends straight away.',
];

export const FAQS = [
  {
    // backend/mcp_app/tools.py: tools return typed results; screenshots are
    // never returned; get_profile returns email, plan and remaining audits.
    q: 'Is my data shared with Anthropic?',
    a: 'Only what a tool returns when Claude uses it. That is your audit list, scores, findings and comparisons, '
      + 'and your account email, plan and remaining audits. Screenshots are never sent. We send nothing else, '
      + 'and nothing at all unless Claude calls one of our tools. How Anthropic handles your conversations is '
      + 'covered by its own policies.',
  },
  {
    // The price is READ from config/pricing.js (no-bare-price-literal), which
    // test_pricing_copy_matches_backend pins to backend/pricing.py;
    // audit/free_quota.py DEFAULT_MONTHLY_FREE = 1.
    q: 'Does it cost extra?',
    a: 'No. Connecting is free. Audits cost the same as on the website: one free audit a month, '
      + `then $${PLAN_PRICES.STARTER.usd} for a full report.`,
  },
  {
    // backend/audit/reaudit.py WINDOW_DAYS and pick_parent; server.py
    // check_audit_limit grants it, and MCP start_audit passes the URL to it.
    q: 'Is the re-audit after my fixes free?',
    a: 'Yes, once for each paid audit: one free re-audit of the same page within 30 days, with the full report. '
      + 'Starting an audit of that page, here or from Claude, uses it automatically.',
  },
  {
    // backend/oauth/config.py DEFAULT_GRANTED excludes credits:spend;
    // backend/mcp_app/tools.py start_paid_audit refuses without confirm_spend.
    q: 'Can Claude spend my credits?',
    a: 'Only with two approvals from you. The permission to spend credits is unticked when you connect, '
      + 'and even with it ticked, Claude must ask you before each paid audit.',
  },
  {
    // frontend/src/components/ConnectedApps.js: Disconnect revokes every token.
    q: 'How do I disconnect?',
    a: 'Open your Account page, find Connected apps, and choose Disconnect next to Claude. '
      + 'That removes its access and signs it out straight away.',
  },
];

export const CLOSING = {
  h2: 'Run your first audit',
  lead: 'Start with your free audit on the website, then pick it up in Claude.',
  cta: 'Start free audit',
};
